/* eslint-disable import/prefer-default-export */
import type {
  AgencySearchProfileCardFragment,
  ContactSearchProfileCardFragment,
} from '../../../../../generated/graphql';

import type {
  SchemaCollectionPage,
  SchemaItemListElement,
  SchemaPostalAddress,
} from '../../../../../@types/schema';
import { buildProfileUrl } from './utils/url';

/**
 * Create a PostalAddress schema for a location/suburb
 */
const makePostalAddressSchema = (
  suburb?: string,
  state?: string,
  postcode?: string,
): SchemaPostalAddress | undefined => {
  if (!suburb) {
    return undefined;
  }

  const postalAddress: SchemaPostalAddress = {
    '@type': 'PostalAddress' as const,
    addressLocality: suburb,
  };

  if (state) {
    postalAddress.addressRegion = state;
  }

  if (postcode) {
    postalAddress.postalCode = postcode;
  }

  return postalAddress;
};

/**
 * Create a RealEstateAgent schema item for an agent from search results
 */
const makeAgentItemSchema = ({
  agent,
  baseUrl,
  position,
  locationProfile,
}: {
  agent: ContactSearchProfileCardFragment;
  baseUrl: string;
  position: number;
  locationProfile?: {
    suburbName?: string | null;
    state?: string | null;
    postcode?: string | null;
  };
}): SchemaItemListElement | null => {
  const phone = agent.mobile || agent.telephone || null;
  const image = agent.profilePhoto || agent.agencyLogoUrl || null;
  const profilePath = agent.profileUrl;

  if (!agent.name || !profilePath) {
    return null;
  }

  const schema: SchemaItemListElement = {
    '@type': 'ListItem' as const,
    position,
    item: {
      '@type': 'RealEstateAgent',
      name: agent.name,
      url: buildProfileUrl(baseUrl, profilePath),
      image,
      telephone: phone,
      ...(locationProfile && {
        address: makePostalAddressSchema(
          locationProfile.suburbName || undefined,
          locationProfile.state || undefined,
          locationProfile.postcode || undefined,
        ),
      }),
    },
  };

  return schema;
};

/**
 * Create a RealEstateAgent schema item for an agency from search results
 */
const makeAgencyItemSchema = ({
  agency,
  baseUrl,
  position,
  locationProfile,
}: {
  agency: AgencySearchProfileCardFragment;
  baseUrl: string;
  position: number;
  locationProfile?: {
    suburbName?: string | null;
    state?: string | null;
    postcode?: string | null;
  };
}): SchemaItemListElement | null => {
  const phone = agency.mobile || agency.telephone || null;
  const image = agency.logoSmall || null;
  const profilePath = agency.profileUrl;

  if (!agency.name || !profilePath) {
    return null;
  }

  const schema: SchemaItemListElement = {
    '@type': 'ListItem' as const,
    position,
    item: {
      '@type': 'RealEstateAgent',
      name: agency.name,
      url: buildProfileUrl(baseUrl, profilePath),
      image,
      telephone: phone,
      ...(locationProfile && {
        address: makePostalAddressSchema(
          locationProfile.suburbName || undefined,
          locationProfile.state || undefined,
          locationProfile.postcode || undefined,
        ),
      }),
    },
  };

  return schema;
};

/**
 * Generate CollectionPage schema for search results
 * Supports both agent and agency search results
 */
export const makeCollectionPageSchema = ({
  baseUrl,
  currentUrl,
  isAgency,
  agentResults,
  agencyResults,
  locationProfiles,
}: {
  baseUrl: string;
  currentUrl: string;
  isAgency: boolean;
  agentResults?: (ContactSearchProfileCardFragment | null)[] | null;
  agencyResults?: (AgencySearchProfileCardFragment | null)[] | null;
  locationProfiles?: Array<{
    suburbName?: string | null;
    state?: string | null;
    postcode?: string | null;
  }> | null;
}): SchemaCollectionPage | null => {
  const results = isAgency ? agencyResults : agentResults;

  if (!results || results.length === 0) {
    return null;
  }

  // Filter out null items
  const filteredResults = results.filter((item) => item !== null);

  if (filteredResults.length === 0) {
    return null;
  }

  // Get the primary location for the page title
  const primaryLocation =
    locationProfiles && locationProfiles.length > 0
      ? locationProfiles[0]?.suburbName
      : undefined;

  // Build the page name and description
  const pageType = isAgency ? 'agencies' : 'agents';
  const pageName = primaryLocation
    ? `Real estate ${pageType} in ${primaryLocation}`
    : `Find real estate ${pageType}`;

  const pageDescription = primaryLocation
    ? `Browse real estate ${pageType} active in ${primaryLocation} and nearby areas`
    : `Search and find real estate ${pageType}`;

  // Create ItemList elements
  const itemListElements: SchemaItemListElement[] = [];

  filteredResults.forEach((result) => {
    const generatedItem = isAgency
      ? makeAgencyItemSchema({
          agency: result as AgencySearchProfileCardFragment,
          baseUrl,
          position: itemListElements.length + 1,
          locationProfile: locationProfiles?.[0],
        })
      : makeAgentItemSchema({
          agent: result as ContactSearchProfileCardFragment,
          baseUrl,
          position: itemListElements.length + 1,
          locationProfile: locationProfiles?.[0],
        });

    if (generatedItem) {
      itemListElements.push(generatedItem);
    }
  });

  if (itemListElements.length === 0) {
    return null;
  }

  const schema: SchemaCollectionPage = {
    '@context': 'http://schema.org',
    '@type': 'CollectionPage',
    name: pageName,
    description: pageDescription,
    url: buildProfileUrl(baseUrl, currentUrl),
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: itemListElements,
    },
  };

  return schema;
};
